import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
// FlowAssistant calls are intentionally omitted when showing modal-only preview
import Questionnaire from '../../../../../../libs/api/services/Questionnaire'
import WorkFlow from '../../../../../../libs/api/services/WorkFlow'
import CommonFunctionsContext from '../../../../../common/CommonFunctionsProvider'
import WorkflowWizard from '../../../../workflow/configuration/facility/wizard/WorkflowWizard'
// FlowAssistant is used elsewhere; keep import available for navigation helpers if needed
// (not used directly in this helper anymore)
// FlowAssistant import retained for potential navigation helpers; currently unused in this file.
// eslint-disable-next-line no-unused-vars

export default function QBuilderAdvancedOptionsEventTrigger({ cat, evt, values, onChangeCategory, onChangeEvent }) {
    // category and event are intentionally kept for compatibility with callers
    // eslint-disable-next-line no-unused-vars
    const [category, setCategory] = useState(cat)
    // eslint-disable-next-line no-unused-vars
    const [event, setEvent] = useState(evt)
    const { showError, showToast } = useContext(CommonFunctionsContext)
    const [isCreatingTrigger, setIsCreatingTrigger] = useState(false)

    const addTask = () =>
        openRedirectModal({ returnPath: '/sys-admin/questionnaires/builder', bookmarkKey: 'QUESTIONAIRE_BUILDER_OPTIONS', payload: values, destination: '/sys-admin/event/new' })

    // reviewTask handled via Review buttons which navigate to the workflows screen or open the wizard modal

    // modal state for confirming redirect/bookmark steps
    const [showModal, setShowModal] = useState(false)
    const [modalParams, setModalParams] = useState(null)

    // trigger selection modal state
    const [showTriggerModal, setShowTriggerModal] = useState(false)
    const [pendingWF, setPendingWF] = useState(null) // { category, event }
    const [linkedTriggers, setLinkedTriggers] = useState([])
    const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null)
    const [selectedTrigger, setSelectedTrigger] = useState(null)
    const [isLoadingTriggers, setIsLoadingTriggers] = useState(false)
    const [linkedWorkflows, setLinkedWorkflows] = useState([])
    const [isLoadingLinkedWorkflows, setIsLoadingLinkedWorkflows] = useState(false)

    const questionnaireId = values?.questionnaireId

    // helper to load linked workflows and current questionnaire
    const modalWasOpen = useRef(false)
    const loadLinkedWorkflows = useCallback(async (qId) => {
        const qid = qId || questionnaireId
        if (!qid) {
            setLinkedWorkflows([])
            setCurrentQuestionnaire(null)
            return
        }
        setIsLoadingLinkedWorkflows(true)
        try {
            const q = await Questionnaire.GetQuestionnaire(qid)
            setCurrentQuestionnaire(q)
        } catch (e) {
            setCurrentQuestionnaire(null)
        }
        try {
            const evts = await WorkFlow.ListEventsByQuestionnaire(qid)
            const mapped = (evts || []).map(e => ({ group: e.group || e.Group, event: e }))
            setLinkedWorkflows(mapped)
        } catch (e) {
            setLinkedWorkflows([])
        } finally {
            setIsLoadingLinkedWorkflows(false)
        }
    }, [questionnaireId])

    useEffect(() => {
        // initial load when questionnaireId is available
        loadLinkedWorkflows()
        // no cleanup required
    }, [questionnaireId, loadLinkedWorkflows])

    // when the WorkflowWizard modal is closed after being opened, refresh the linked list
    useEffect(() => {
        if (showModal) {
            modalWasOpen.current = true
            return
        }
        if (!showModal && modalWasOpen.current) {
            // modal transitioned from open -> closed
            modalWasOpen.current = false
            // refresh the linked workflows for current questionnaire
            try { loadLinkedWorkflows() } catch (e) { }
        }
    }, [showModal, loadLinkedWorkflows])

    const _triggerDisplayName = (t) => {
        try {
            const key = t?.type?.key || t?.type?.type?.key || (t?.type && t?.type.name && t.type.name.toString().toUpperCase())
            if (key) {
                const map = {
                    'QUESIONNAIRE_STARTED': 'When Started',
                    'QUESIONNAIRE_COMPLETE': 'When completed',
                    'QUESIONNAIRE_ALERT_TRIGGERED': 'Alert has been triggered',
                    'QUESIONNAIRE_INCOMPLETE_FOR_A_PERIOD': 'Incomplete for a period of time',
                }
                if (map[key]) return map[key]
            }
        } catch (e) { }
        return t?.type?.name ?? t?.type?.group?.group ?? 'Trigger'
    }

    function openRedirectModal(params) {
        setModalParams(params)
        setShowModal(true)
    }

    function _openReviewModal(wfCategory, wfEvent) {
        // Open the WorkflowWizard in the fullscreen modal for review, seeded with the chosen category/event
        try {
            const subsection = {
                values: { category: wfCategory, event: wfEvent },
                setValues: (s) => {
                    try { if (s.category) setCategory(s.category) } catch (e) { }
                    try { if (s.event) setEvent(s.event) } catch (e) { }
                    try { if (s.category) onChangeCategory?.(s.category) } catch (e) { }
                    try { if (s.event) onChangeEvent?.(s.event) } catch (e) { }
                },
                navigation: () => { }
            }
            setModalParams({ destination: '/sys-admin/event/new', subsection })
            setShowModal(true)
        } catch (e) {
            try { console.error(e) } catch (err) { }
        }
    }

    async function _openTriggerSelectionModal(wfCategory, wfEvent) {
        setPendingWF({ category: wfCategory, event: wfEvent })
        setIsLoadingTriggers(true)
        try {
            // Prefer normalized endpoint returning { type, triggers }
            const list = await WorkFlow.ListTriggerTypesLinkedNormalized(typeof wfEvent === 'object' ? wfEvent.id : JSON.parse(wfEvent).id)
            // Safely map to expected shape (type, triggers) with fallbacks
            const normalized = (list || []).map(it => ({ original: it, type: it.type || it.Type || it.Type?.type || it.type?.type || it, triggers: it.triggers || it.Triggers || it.trigger || it.Trigger || it.link || it.Link || [] }))
            const filtered = normalized.filter(t => {
                try {
                    const g = t.type && (t.type.group?.group || t.type.Group?.Group) ? (t.type.group?.group || t.type.Group?.Group).toString().toLowerCase() : ''
                    const n = t.type && (t.type.name || t.type.Name) ? (t.type.name || t.type.Name).toString().toLowerCase() : ''
                    return g.includes('question') || n.includes('questionnaire') || n.includes('question')
                } catch (e) { return false }
            })
            setLinkedTriggers(filtered)
        } catch (e) {
            setLinkedTriggers([])
        } finally {
            setIsLoadingTriggers(false)
        }
        // load the questionnaire being edited in the builder (if available)
        try {
            if (values && values.questionnaireId) {
                const q = await Questionnaire.GetQuestionnaire(values.questionnaireId)
                setCurrentQuestionnaire(q)
            } else {
                setCurrentQuestionnaire(null)
            }
        } catch (e) {
            setCurrentQuestionnaire(null)
        }
        // If a questionnaire is open in the builder, find any existing workflow events that reference it
        try {
            setLinkedWorkflows([])
            if (values && values.questionnaireId) {
                await loadLinkedWorkflows(values.questionnaireId)
            }
        } catch (e) {
            setLinkedWorkflows([])
        }
        setShowTriggerModal(true)
    }


    // confirmRedirect removed: modal now serves as an in-place preview of the destination component.

    return (
        <div>
            <div className='d-flex'>
                <div style={{ cursor: 'pointer' }} className='w-100 text-center bg-secondary-container p-1 rounded shadow'>Existing Event</div>
                <div onClick={addTask} style={{ cursor: 'pointer' }} className='w-100 text-center d-flex flex-column justify-content-center'>Create A New Event</div>
            </div>

            {/* If workflows are linked to the active questionnaire, show them here as a quick-selection *instead* of the Category/Event pickers */}
            {isLoadingLinkedWorkflows && (
                <div className="d-flex justify-content-center my-3">
                    <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                </div>
            )}

            {!isLoadingLinkedWorkflows && linkedWorkflows && linkedWorkflows.length > 0 ? (
                <div className='mb-3'>
                    <div className='mb-1'><b>Workflows linked to this questionnaire</b></div>
                    <div className='d-flex flex-column mb-2'>
                        {linkedWorkflows.map((m, i) => (
                            <div key={`main-linked-${i}`} className='p-2 border rounded mb-1 d-flex justify-content-between align-items-center'>
                                <div>
                                    <div><b>{m.event.result ?? m.event.name ?? m.event.title}</b></div>
                                    <div className='text-muted'>{m.group?.group ?? m.group?.name}</div>
                                </div>
                                <div>
                                    <button className='btn btn-sm btn-outline-primary' onClick={() => {
                                        // open the workflow in the modal for review
                                        _openReviewModal(m.group, m.event)
                                    }}>Review</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className=' text-center mb-3 p-3'>
                    Select "Create A New Event" to link a new workflow event to this questionnaire.
                </div>
            )}
            {/* Redirect confirmation modal */}
            {modalParams && (
                <Modal show={showModal} onHide={() => setShowModal(false)} fullscreen size="xl">
                    <Modal.Header closeButton>
                        <Modal.Title></Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh', padding: '1rem' }}>
                        {/* If the destination is the event/new route, render the actual components used on that page */}
                        {modalParams.destination === '/sys-admin/event/new' ? (
                            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                <WorkflowWizard modalMode={true} subsection={modalParams.subsection} onUseWorkflow={({ category: wfCategory, event: wfEvent }) => {
                                    // Instead of immediately closing, open trigger selection modal so the user
                                    // can pick which questionnaire trigger to link this workflow event to.
                                    _openTriggerSelectionModal(wfCategory, wfEvent)
                                }} />
                            </div>
                        ) : (
                            <>
                                <label>Payload preview</label>
                                <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(modalParams.payload, null, 2)}</pre>
                            </>
                        )}
                    </Modal.Body>
                    {/* Footer buttons removed - modal can be closed via the header close button */}
                </Modal>
            )}
            {/* Trigger selection modal: pick which trigger to link the questionnaire to */}
            <Modal show={showTriggerModal} onHide={() => setShowTriggerModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Link Questionnaire to Workflow Trigger</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingTriggers && (
                        <div className="d-flex justify-content-center my-3">
                            <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                        </div>
                    )}

                    {/* Workflows already linked to this questionnaire (quick access) */}
                    {isLoadingLinkedWorkflows && (
                        <div className="d-flex justify-content-center my-3">
                            <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                        </div>
                    )}

                    {!isLoadingLinkedWorkflows && linkedWorkflows && linkedWorkflows.length > 0 && (
                        <div className='mb-3'>
                            <div className='mb-1'><b>Workflows linked to this questionnaire</b></div>
                            <div className='d-flex flex-column mb-2'>
                                {linkedWorkflows.map((m, i) => (
                                    <div key={`linked-${i}`} className='p-2 border rounded mb-1 d-flex justify-content-between align-items-center'>
                                        <div>
                                            <div><b>{m.event.result ?? m.event.name ?? m.event.title}</b></div>
                                            <div className='text-muted'>{m.group?.group ?? m.group?.name}</div>
                                        </div>
                                        <div>
                                            <button className='btn btn-sm btn-outline-primary' onClick={() => {
                                                // close trigger modal and open review modal for this workflow
                                                try { setShowTriggerModal(false) } catch (e) { }
                                                _openReviewModal(m.group, m.event)
                                            }}>Review</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isLoadingTriggers && (!linkedTriggers || linkedTriggers.length === 0) && (
                        <div>No questionnaire-related triggers available for the selected workflow event.</div>
                    )}

                    {!isLoadingTriggers && linkedTriggers && linkedTriggers.length > 0 && (
                        <div>
                            <div className='mb-2'><b>Select a trigger type</b></div>
                            <div className='d-flex flex-column mb-3'>
                                {linkedTriggers.map((t, i) => (
                                    <div key={i} className={'p-2 border rounded mb-1' + (selectedTrigger === t ? ' bg-primary text-white' : '')} style={{ cursor: isLoadingTriggers ? 'default' : 'pointer' }} onClick={() => { if (!isLoadingTriggers) setSelectedTrigger(t) }}>
                                        <b>{_triggerDisplayName(t)}</b>
                                        <div className='text-muted'>{(t.type && (t.type.group?.group || t.type.Group?.Group)) ?? 'Questionnaire/Survey/Form Events'}</div>
                                    </div>
                                ))}
                            </div>

                            {selectedTrigger && (
                                <>
                                    <div className='mb-2'><b>Questionnaire to link</b></div>
                                    <div className='d-flex flex-column'>
                                        <div className='p-2 border rounded mb-1 d-flex justify-content-between align-items-center'>
                                            <div>{currentQuestionnaire ? currentQuestionnaire.name : 'No questionnaire open in builder'}</div>
                                            <div>
                                                <button className='btn btn-sm btn-primary' disabled={isCreatingTrigger || isLoadingTriggers} onClick={async () => {
                                                    try {
                                                        setIsCreatingTrigger(true)
                                                        if (!values || !values.questionnaireId) {
                                                            showError('No questionnaire present in builder values')
                                                            return
                                                        }
                                                        // determine trigger id from normalized shape (triggers array)
                                                        const eventId = typeof pendingWF.event === 'object' ? pendingWF.event.id : JSON.parse(pendingWF.event).id
                                                        const triggerArr = (selectedTrigger && Array.isArray(selectedTrigger.triggers)) ? selectedTrigger.triggers : (selectedTrigger && Array.isArray(selectedTrigger.Triggers) ? selectedTrigger.Triggers : [])
                                                        const triggerCandidate = triggerArr.length ? triggerArr[0] : null
                                                        let triggerId = triggerCandidate && (triggerCandidate.id || triggerCandidate.Id || triggerCandidate.triggerId || triggerCandidate.linkId) ? (triggerCandidate.id || triggerCandidate.Id || triggerCandidate.triggerId || triggerCandidate.linkId) : null

                                                        // If there is no existing trigger instance on the event, attempt to create/attach one
                                                        if (!triggerId) {
                                                            // get the trigger type id
                                                            const typeId = selectedTrigger && selectedTrigger.type && (selectedTrigger.type.id || selectedTrigger.type.Id)
                                                            if (!typeId) {
                                                                showError('No trigger type id available for this trigger')
                                                                return
                                                            }
                                                            // call server to toggle (create) the trigger instance; this returns the new TriggerId
                                                            const created = await WorkFlow.ToggleEventTaskType(eventId, typeId)
                                                            // log the raw created response to the browser console for debugging
                                                            try { console.log('ToggleEventTaskType response', created) } catch (e) { }
                                                            if (!created) {
                                                                showError('Failed to create trigger instance for this event')
                                                                return
                                                            }
                                                            // WorkFlow.ToggleEventTaskType returns the created Guid (or null). Use it as the triggerId.
                                                            triggerId = created

                                                            // Refresh the linked triggers list so the UI shows the newly created trigger instance
                                                            try {
                                                                const refList = await WorkFlow.ListTriggerTypesLinkedNormalized(eventId)
                                                                const refNormalized = (refList || []).map(it => ({ original: it, type: it.type || it.Type || it.Type?.type || it.type?.type || it, triggers: it.triggers || it.Triggers || it.trigger || it.Trigger || it.link || it.Link || [] }))
                                                                setLinkedTriggers(refNormalized.filter(t => {
                                                                    try {
                                                                        const g = t.type && (t.type.group?.group || t.type.Group?.Group) ? (t.type.group?.group || t.type.Group?.Group).toString().toLowerCase() : ''
                                                                        const n = t.type && (t.type.name || t.type.Name) ? (t.type.name || t.type.Name).toString().toLowerCase() : ''
                                                                        return g.includes('question') || n.includes('questionnaire') || n.includes('question')
                                                                    } catch (e) { return false }
                                                                }))
                                                                // attempt to re-select the trigger type we created so downstream code uses the refreshed triggers array
                                                                const matching = refNormalized.find(r => (r.type && (r.type.id === typeId || r.type.Id === typeId || r.type.type === typeId || r.type.Type === typeId)))
                                                                if (matching) setSelectedTrigger(matching)
                                                            } catch (e) { }
                                                        }
                                                        // deduce property name robustly by inspecting multiple type/key/name variants
                                                        const typeObj = selectedTrigger.type || selectedTrigger.Type || selectedTrigger.original?.type || selectedTrigger.original?.Type || {}
                                                        const typeTextCandidates = [
                                                            typeObj.name,
                                                            typeObj.Name,
                                                            typeObj.key,
                                                            typeObj.Key,
                                                            typeObj.type && (typeObj.type.name || typeObj.type.Name),
                                                            selectedTrigger.original && (selectedTrigger.original.type?.name || selectedTrigger.original.Type?.Name)
                                                        ].filter(Boolean).map(s => s.toString().toLowerCase()).join(' ')

                                                        let property = 'questionnaire_started'
                                                        // prefer explicit keywords in text or the key value
                                                        const keyUpper = (typeObj.key || typeObj.Key || '').toString().toUpperCase()
                                                        if (typeTextCandidates.includes('complete') || typeTextCandidates.includes('completed') || keyUpper.includes('COMPLETE')) property = 'questionnaire_complete'
                                                        else if (typeTextCandidates.includes('alert') || keyUpper.includes('ALERT')) property = 'questionnaire_alert'
                                                        else if (typeTextCandidates.includes('time') || typeTextCandidates.includes('incomplete') || typeTextCandidates.includes('lapse') || typeTextCandidates.includes('lapsed') || keyUpper.includes('INCOMPLETE')) property = 'questionnaire_incomplete_for_x'

                                                        // fetch the questionnaire object to store as linked object
                                                        const qraw = await Questionnaire.GetQuestionnaire(values.questionnaireId)
                                                        // Normalize to a minimal shape other parts of the app expect.
                                                        // Try many fallbacks for id/name in case the API returns different envelopes.
                                                        const qid = qraw?.id ?? qraw?.Id ?? qraw?.questionnaire?.id ?? qraw?.questionnaire?.Id ?? qraw?.questionnaireId ?? values?.questionnaireId
                                                        const qname = qraw?.name ?? qraw?.Name ?? qraw?.questionnaire?.name ?? qraw?.questionnaire?.Name ?? qraw?.title ?? qraw?.displayName ?? values?.questionnaireName ?? values?.questionnaireTitle ?? values?.questionnaire?.name
                                                        // Save both camelCase and PascalCase keys to maximize compatibility with
                                                        // backend deserialization (Json.NET) and older frontend consumers.
                                                        const q = {
                                                            id: qid,
                                                            Id: qid,
                                                            name: qname || 'Unnamed questionnaire',
                                                            Name: qname || 'Unnamed questionnaire',
                                                            type: qraw?.type ?? qraw?.questionnaire?.type ?? 'Questionnaire',
                                                            Type: qraw?.type ?? qraw?.questionnaire?.type ?? 'Questionnaire'
                                                        }

                                                        // ensure triggerId is a primitive (string/guid). ToggleEventTaskType may return the Guid directly
                                                        // or (rarely) an object; tolerate both shapes.
                                                        if (typeof triggerId === 'object' && triggerId !== null) {
                                                            triggerId = triggerId.id || triggerId.Id || triggerId.value || triggerId.triggerId || JSON.stringify(triggerId)
                                                        }

                                                        const setRes = await WorkFlow.SetTriggerProperty(eventId, triggerId, {
                                                            property: property,
                                                            value: JSON.stringify([q])
                                                        })
                                                        try { console.log('SetTriggerProperty response', setRes) } catch (e) { }

                                                        // Read the property back to validate what was stored and log it
                                                        try {
                                                            const saved = await WorkFlow.GetTriggerPropertyByType(eventId, triggerId, property)
                                                            try { console.log('GetTriggerPropertyByType after save', saved) } catch (e) { }
                                                            if (saved && saved.value) {
                                                                let parsed = []
                                                                try { parsed = JSON.parse(saved.value) } catch (e) { parsed = [] }
                                                                let needUpdate = false
                                                                parsed = (Array.isArray(parsed) ? parsed : []).map(item => {
                                                                    const nid = item?.id ?? item?.Id ?? q.id
                                                                    const nname = item?.name ?? item?.Name ?? q.name
                                                                    if (!item?.name || !item?.id || !item?.Id || !item?.Name) needUpdate = true
                                                                    return {
                                                                        id: nid,
                                                                        Id: nid,
                                                                        name: nname,
                                                                        Name: nname,
                                                                        type: item?.type ?? item?.Type ?? q.type,
                                                                        Type: item?.type ?? item?.Type ?? q.type
                                                                    }
                                                                })
                                                                if (needUpdate) {
                                                                    await WorkFlow.SetTriggerProperty(eventId, triggerId, { property, value: JSON.stringify(parsed) })
                                                                    try { console.log('Re-wrote trigger property with normalized value', parsed) } catch (e) { }
                                                                }
                                                            }
                                                        } catch (e) {
                                                            // non-fatal - ignore validation/readback errors
                                                        }

                                                        // show success toast
                                                        try { showToast('Questionnaire linked to workflow trigger', undefined, 3) } catch (e) { }

                                                        // refresh linked workflows list so UI reflects the newly linked event
                                                        try { await loadLinkedWorkflows(values?.questionnaireId) } catch (e) { }

                                                        // finalize: set the category/event on the builder and close modals
                                                        try { setCategory(pendingWF.category) } catch (e) { }
                                                        try { setEvent(pendingWF.event) } catch (e) { }
                                                        onChangeCategory?.(pendingWF.category)
                                                        onChangeEvent?.(pendingWF.event)

                                                        setShowTriggerModal(false)
                                                        setShowModal(false)
                                                        setModalParams(null)
                                                        setPendingWF(null)
                                                        setSelectedTrigger(null)
                                                    } catch (err) {
                                                        showError(err)
                                                    } finally {
                                                        setIsCreatingTrigger(false)
                                                    }
                                                }}>
                                                    {isCreatingTrigger && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                                    Link
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    )
}
